import React, { useEffect, useMemo } from "react";
import { Icon, Button, Col, RSelect } from "../../components/Component";
import { Modal, ModalBody, Form } from "reactstrap";
import { useForm } from "react-hook-form";
import { handlePostApis } from "../../service/Api";
import { filterStatus } from "../../service/Data";
import { mediaUrl } from "../../components/content/contentApi";

// Preview for a picked File (object URL, freed on change) or a saved URL.
function usePreview(value) {
  const url = useMemo(() => {
    if (!value) return null;
    return typeof value === "string" ? mediaUrl(value) : URL.createObjectURL(value);
  }, [value]);
  useEffect(() => () => {
    if (url && typeof value !== "string") URL.revokeObjectURL(url);
  }, [url, value]);
  return url;
}

const FormModal = ({ modal, closeModal, fetchData, modalType, formData, setFormData }) => {
  const { reset, handleSubmit } = useForm();
  const imageUrl = usePreview(formData.image);
  const mobileImageUrl = usePreview(formData.mobile_image);

  // Reset form when formData changes (edit / add)
  useEffect(() => {
    reset(formData);
  }, [formData, reset]);

  const handleFile = (field) => (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (file) setFormData({ ...formData, [field]: file, ...(field === "mobile_image" ? { remove_mobile_image: false } : {}) });
  };

  const removeMobileImage = () => setFormData({ ...formData, mobile_image: "", remove_mobile_image: true });

  // Add Banner
  const handleAddBanner = async () => {
    if (!formData.image) {
      alert("Image is required");
      return;
    }

    const data = new FormData();
    data.append("image", formData.image);
    if (formData.mobile_image && typeof formData.mobile_image !== "string") {
      data.append("mobile_image", formData.mobile_image);
    }
    data.append("title", formData.title || "");
    data.append("description", formData.description || "");
    data.append("button_text", formData.button_text || "");
    data.append("button_link", formData.button_link || "");
    data.append("position", formData.position || 1);
    data.append("is_active", formData.is_active ? 1 : 0);

    try {
      const res = await handlePostApis("admin/add/banner", data);
      alert(res.data.message);
      fetchData();
      closeModal();
    } catch (error) {
      console.error(error);
      alert("Failed to add banner");
    }
  };

  // Update Banner
  const handleUpdateBanner = async () => {
    if (!formData.id) {
      alert("Banner ID is missing");
      return;
    }

    const data = new FormData();
    data.append("id", formData.id);
    data.append("is_active", formData.is_active ? 1 : 0);

    // If admin uploaded a new image during edit
    if (formData.image && typeof formData.image !== "string") {
      data.append("image", formData.image);
    }
    if (formData.mobile_image && typeof formData.mobile_image !== "string") {
      data.append("mobile_image", formData.mobile_image);
    } else if (formData.remove_mobile_image) {
      data.append("remove_mobile_image", 1);
    }

    data.append("title", formData.title || "");
    data.append("description", formData.description || "");
    data.append("button_text", formData.button_text || "");
    data.append("button_link", formData.button_link || "");
    data.append("position", formData.position || 1);

    try {
      const res = await handlePostApis("admin/update/banner", data);
      alert(res.data.message);
      fetchData();
      closeModal();
    } catch (error) {
      console.error(error);
      alert("Failed to update banner");
    }
  };

  return (
    <Modal isOpen={modal} toggle={closeModal} className="modal-dialog-centered" size="lg">
      <ModalBody>
        <a href="#cancel" onClick={(ev) => { ev.preventDefault(); closeModal(); }} className="close">
          <Icon name="cross-sm" />
        </a>

        <div className="p-2">
          <h5 className="title">{modalType === "add" ? "Add Banner" : "Update Banner"}</h5>

          <div className="mt-4">
            <Form
              className="row gy-4"
              onSubmit={handleSubmit(() => modalType === "add" ? handleAddBanner() : handleUpdateBanner())}
            >

              {/* Desktop image (required) */}
              <div className="col-md-7">
                <div className="form-group">
                  <label className="form-label">{modalType === "add" ? "Desktop image" : "Replace desktop image"}</label>
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/avif" onChange={handleFile("image")} className="form-control" />
                  <div className="form-note mt-1">Landscape, about 1920 × 900 px. Shown on laptops & tablets.</div>
                </div>
                {imageUrl && <img src={imageUrl} alt="Desktop banner" className="mt-2 rounded border" style={{ width: "100%", aspectRatio: "16 / 7.5", objectFit: "cover" }} />}
              </div>

              {/* Mobile image (optional) */}
              <div className="col-md-5">
                <div className="form-group">
                  <label className="form-label">Mobile image <span className="text-soft">(optional)</span></label>
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/avif" onChange={handleFile("mobile_image")} className="form-control" />
                  <div className="form-note mt-1">Portrait 4:5, about 1080 × 1350 px. On phones it shows in full above the banner text. Empty = the desktop image is cropped to fit.</div>
                </div>
                {mobileImageUrl && (
                  <div className="mt-2 d-flex align-items-start gap-2">
                    <img src={mobileImageUrl} alt="Mobile banner" className="rounded border" style={{ width: 120, aspectRatio: "4 / 5", objectFit: "cover" }} />
                    <Button size="sm" color="light" outline type="button" onClick={removeMobileImage} title="Remove mobile image">
                      Remove
                    </Button>
                  </div>
                )}
              </div>

              {/* Status */}
              <Col md="12">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <div className="form-control-wrap">
                    <RSelect
                      options={filterStatus}
                      value={{
                        value: formData.is_active,
                        label: formData.is_active ? "Active" : "Inactive",
                      }}
                      onChange={(e) => setFormData({ ...formData, is_active: e.value })}
                    />
                  </div>
                </div>
              </Col>

              {/* Title */}
              <Col md="12">
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.title || ""}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
              </Col>

              {/* Description */}
              <Col md="12">
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    value={formData.description || ""}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
              </Col>

              {/* Button Text */}
              <Col md="6">
                <div className="form-group">
                  <label className="form-label">Button Text</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.button_text || ""}
                    onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                  />
                </div>
              </Col>

              {/* Button Link */}
              <Col md="6">
                <div className="form-group">
                  <label className="form-label">Button Link</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.button_link || ""}
                    onChange={(e) => setFormData({ ...formData, button_link: e.target.value })}
                  />
                </div>
              </Col>

              {/* Position */}
              <Col md="6">
                <div className="form-group">
                  <label className="form-label">Position</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.position || ""}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  />
                </div>
              </Col>

              {/* Buttons */}
              <Col size="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button color="primary" size="md" type="submit">
                      {modalType === "add" ? "Submit" : "Update"}
                    </Button>
                  </li>
                  <li>
                    <Button
                      onClick={(ev) => { ev.preventDefault(); closeModal(); }}
                      className="link link-light"
                    >
                      Cancel
                    </Button>
                  </li>
                </ul>
              </Col>

            </Form>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default FormModal;